<template>
  <div class="position-relative elevation-4">
    <v-btn
      data-testid="retract-button"
      class="retract-button"
      @click="emitHide"
    >
      <v-icon color="primary">
        mdi-chevron-right
      </v-icon>
    </v-btn>
    <shared-map
      :drawing-tool-cursor="drawingToolCursor"
      @mousedown="handleMouseDown"
      @mouseup="handleMouseUp"
      @mousemove="handleMapMouseMove"
      @l-map-ready="setLMap"
    >
      <template #map>
        <station-marker-cluster
          v-if="clusterReady"
          :map="lMap"
          :stations-with-status="allStationsWithStatus"
        />
        <station-circle-selector
          v-if="currentTool === Tool.circle && toolAnchorStart && toolAnchorEnd"
          :map="lMap"
          :start-anchor="toolAnchorStart"
          :end-anchor="toolAnchorEnd"
          :finished="toolState === ToolState.finished"
          :purpose="toolPurpose"
          data-testid="station-map-circle-selector"
          @remove="resetTools"
        />
        <station-rectangle-selector
          v-if="
            currentTool === Tool.rectangle && toolAnchorStart && toolAnchorEnd
          "
          :map="lMap"
          :start-anchor="toolAnchorStart"
          :end-anchor="toolAnchorEnd"
          :finished="toolState === ToolState.finished"
          :purpose="toolPurpose"
          data-testid="station-map-rectangle-selector"
          @remove="resetTools"
        />
      </template>
      <template #tools>
        <tool-button-with-menu
          class="ml-4"
          test-id="rectangle"
          :items="rectangleToolItems"
        />
        <tool-button-with-menu
          class="ml-4"
          test-id="pin"
          :items="circleToolItems"
        />
        <v-tooltip
          :text="t('station_map.deselect-all-stations')"
          bottom
          :open-delay="200"
        >
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              :class="'ml-4 map-button zamg-button-outline-primary'"
              variant="outlined"
              elevation="2"
              data-testid="station-map-delete"
              @click="handleUnselectStations"
            >
              <v-icon color="primary">
                mdi-delete-outline
              </v-icon>
            </v-btn>
          </template>
        </v-tooltip>
        <text-field-with-date-picker
          :value="stationStore.filters.startDate || null"
          :testid="'map-start-date'"
          :allowed-date-range="[
            datasetStore.metadata?.startDate,
            datasetStore.metadata?.endDate,
          ]"
          :text-field-props="{
            style: 'margin-left: 16px; width: 190px; max-width: 190px;',
            variant: 'solo',
            density: 'compact',
            placeholder: t('start_date'),
          }"
          extra-small-height
          :selected-date-is-start-date="true"
          :reference-date="stationStore.filters.endDate"
          @date-selected="handleStartDateSelected"
        />
        <text-field-with-date-picker
          :value="stationStore.filters.endDate || null"
          :testid="'map-end-date'"
          :allowed-date-range="[
            datasetStore.metadata?.startDate,
            datasetStore.metadata?.endDate,
          ]"
          :text-field-props="{
            style: 'margin-left: 16px; width: 190px; max-width: 190px;',
            variant: 'solo',
            density: 'compact',
            placeholder: t('end_date'),
          }"
          extra-small-height
          :selected-date-is-start-date="false"
          :reference-date="stationStore.filters.startDate"
          @date-selected="handleEndDateSelected"
        />
      </template>
    </shared-map>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted, nextTick } from "vue";
import L, { FitBoundsOptions, LatLng, LeafletMouseEvent } from "leaflet";
import type { Map as LeafletMap } from 'leaflet';
import StationStore from "@/stores/station/station-module";
import DatasetStore from "@/stores/dataset/dataset-module";
import MapStore from "@/stores/map/map-module";
import { Station } from "@/stores/station/station-model";
import { Tool, ToolPurpose, ToolState } from "@/stores/map/map-model";
import StationRectangleSelector from "@/components/station/stationMap/StationRectangleSelector.vue";
import StationCircleSelector from "@/components/station/stationMap/StationCircleSelector.vue";
import StationMarkerCluster from "@/components/station/stationMap/StationMarkerCluster.vue";
import TextFieldWithDatePicker from "@/components/common/TextFieldWithDatePicker.vue";
import ToolButtonWithMenu, { ToolItem } from "@/components/common/ToolButtonWithMenu.vue";
import SharedMap from "@/components/common/map/Map.vue";
import { LMap } from "@vue-leaflet/vue-leaflet";
import { useI18n } from "vue-i18n";

interface LMapWithMapObject extends InstanceType<typeof LMap> {
  mapObject: L.Map
}

export default defineComponent({
  name: "StationMap",
  components: {
    StationCircleSelector,
    StationRectangleSelector,
    SharedMap,
    StationMarkerCluster,
    TextFieldWithDatePicker,
    ToolButtonWithMenu,
  },
  props: {
    visible: { type: Boolean, required: true },
  },
  emits: ["hide"],
  setup(props, { emit }) {
    const { t } = useI18n();

    const zoomOptions: FitBoundsOptions = {
      maxZoom: 13,
      padding: [50, 50],
      animate: false,
    };

    const lMap = ref<LMapWithMapObject | null>(null);
    const leafletMap = computed<LeafletMap | null>(() => lMap.value?.mapObject ?? null);

    const datasetStore = DatasetStore;
    const stationStore = StationStore;
    const mapStore = MapStore;

    const clusterReady = ref(false);

    const ToolConst = Tool;
    const ToolStateConst = ToolState;

    const currentTool = ref<Tool>(Tool.none);
    const rectangleToolPurpose = ref<ToolPurpose>(ToolPurpose.replace);
    const circleToolPurpose = ref<ToolPurpose>(ToolPurpose.replace);
    const toolState = ref<ToolState>(ToolState.finished);

    const toolAnchorStart = ref<LatLng | null>(null);
    const toolAnchorEnd = ref<LatLng | null>(null);

    const drawingToolCursor = computed<'crosshair' | 'none' | ''>(() => {
      if (
        (currentTool.value === Tool.rectangle || currentTool.value === Tool.circle) &&
        toolState.value === ToolState.chooseAnchorStart
      ) return 'crosshair';

      if (
        (currentTool.value === Tool.rectangle || currentTool.value === Tool.circle) &&
        toolState.value === ToolState.chooseAnchorEnd
      ) return 'none';

      return '';
    });

    const rectangleToolItems = computed<Array<ToolItem>>(() => [
      {
        value: t("station_map.replace_selection"),
        icon: "custom:zamgRectangle",
        testId: "replace-rectangle-selection",
        callback: () => {
          updateTool(Tool.rectangle);
          updateToolState(ToolState.chooseAnchorStart);
          updateToolPurpose(ToolPurpose.replace);
        },
      },
      {
        value: t("station_map.add_selection"),
        icon: "custom:zamgRectangleAdd",
        testId: "add-rectangle-selection",
        callback: () => {
          updateTool(Tool.rectangle);
          updateToolState(ToolState.chooseAnchorStart);
          updateToolPurpose(ToolPurpose.add);
        },
      },
      {
        value: t("station_map.remove_selection"),
        icon: "custom:zamgRectangleRemove",
        testId: "remove-rectangle-selection",
        callback: () => {
          updateTool(Tool.rectangle);
          updateToolState(ToolState.chooseAnchorStart);
          updateToolPurpose(ToolPurpose.remove);
        },
      },
    ]);

    const circleToolItems = computed<Array<ToolItem>>(() => [
      {
        value: t("station_map.replace_selection"),
        icon: "custom:zamgPin",
        testId: "replace-pin-selection",
        callback: () => {
          updateTool(Tool.circle);
          updateToolState(ToolState.chooseAnchorStart);
          updateToolPurpose(ToolPurpose.replace);
        },
      },
      {
        value: t("station_map.add_selection"),
        icon: "custom:zamgPinAdd",
        testId: "add-pin-selection",
        callback: () => {
          updateTool(Tool.circle);
          updateToolState(ToolState.chooseAnchorStart);
          updateToolPurpose(ToolPurpose.add);
        },
      },
      {
        value: t("station_map.remove_selection"),
        icon: "custom:zamgPinRemove",
        testId: "remove-pin-selection",
        callback: () => {
          updateTool(Tool.circle);
          updateToolState(ToolState.chooseAnchorStart);
          updateToolPurpose(ToolPurpose.remove);
        },
      },
    ]);

    function emitHide(): void {
      //  for linter
      emit("hide");
    }

    watch(
      () => props.visible,
      async (visible) => {
        if (visible && leafletMap.value) {
          document.addEventListener("keyup", handleKeyUp);
          // recalc map size when it becomes visible
          await nextTick();
          leafletMap.value.invalidateSize();
        } else {
          document.removeEventListener("keyup", handleKeyUp);
        }
      }
    );

    // When leaflet map exists, invalidate it to reload tiles
    watch(leafletMap, (newMap) => {
      newMap?.invalidateSize()
    });

    watch(toolState, (state) => {
      if (state === ToolState.chooseAnchorStart) {
        mapStore.setPreviouslySelectedStations(stationStore.selectedStations);
      }
    });

    onMounted(async () => {
      document.addEventListener("keyup", handleKeyUp);
      await nextTick();
      if (stationStore.allStations.length > 0) {
        zoomOntoStations();
      }
      // clusterReady.value = true;
    });

    const toolPurpose = computed<ToolPurpose>(() => {
      if (currentTool.value === Tool.rectangle) return rectangleToolPurpose.value;
      if (currentTool.value === Tool.circle) return circleToolPurpose.value;
      return ToolPurpose.none;
    });

    const activeStations = computed<Station[]>(() => {
      if (toolState.value === ToolState.finished) {
        return stationStore.selectedStations;
      }
      if (toolPurpose.value === ToolPurpose.replace) {
        return mapStore.markedStations;
      }
      if (toolPurpose.value === ToolPurpose.add) {
        return stationStore.allStations.filter(
          (station) =>
            mapStore.markedStationsIds.has(station.id) ||
            stationStore.selectedStationsIds.has(station.id)
        );
      }
      if (toolPurpose.value === ToolPurpose.remove) {
        return stationStore.allStations.filter(
          (station) =>
            !mapStore.markedStationsIds.has(station.id) &&
            stationStore.selectedStationsIds.has(station.id)
        );
      }
      return [];
    });

    const inactiveStations = computed<Station[]>(() => {
      const activeSet = new Set<string>(activeStations.value.map((s) => s.id));
      const prevSelectedSet = new Set<string>(
        previouslySelectedStations.value.map((s) => s.id)
      );
      return stationStore.allStations.filter(
        (station) =>
          !activeSet.has(station.id) && !prevSelectedSet.has(station.id)
      );
    });

    const previouslySelectedStations = computed<Station[]>(() => {
      if (
        toolPurpose.value === ToolPurpose.replace &&
        toolState.value !== ToolState.finished
      ) {
        const activeSet = new Set<string>(activeStations.value.map((s) => s.id));
        return mapStore.previouslySelectedStations.filter(
          (station) => !activeSet.has(station.id)
        );
      } else {
        return [];
      }
    });

    const allStationsWithStatus = computed(() => {
      return [
        ...activeStations.value.map(s => ({ station: s, status: 'active' })),
        ...inactiveStations.value.map(s => ({ station: s, status: 'inactive' })),
        ...previouslySelectedStations.value.map(s => ({ station: s, status: 'previously-active' })),
      ];
    });

    const setLMap = (map: LMapWithMapObject) => {
      lMap.value = map;
      clusterReady.value = true;
    };

    function updateTool(tool: Tool): void {
      currentTool.value = tool;
    }

    function updateToolState(state: ToolState): void {
      toolState.value = state;
    }

    function updateToolPurpose(purpose: ToolPurpose): void {
      if (currentTool.value === Tool.rectangle) {
        rectangleToolPurpose.value = purpose;
      }
      if (currentTool.value === Tool.circle) {
        circleToolPurpose.value = purpose;
      }
    }

    function handleUnselectStations(): void {
      stationStore.setSelectedStations([]);
    }

    function handleKeyUp(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        if (toolState.value === ToolState.chooseAnchorStart) {
          updateToolState(ToolState.finished);
        }
        e.preventDefault();
      }
    }

    function handleMouseDown(event: LeafletMouseEvent): void {
      if (event.originalEvent.button !== 0) return; // catch only left click
      if (toolState.value === ToolState.chooseAnchorStart) {
        event.originalEvent.stopPropagation();
        leafletMap.value?.dragging.disable();
        toolAnchorStart.value = event.latlng;
        toolState.value = ToolState.chooseAnchorEnd;
      }
    }

    function handleMouseUp(event: LeafletMouseEvent): void {
      if (event.originalEvent.button !== 0) return; // catch only left click
      if (toolState.value === ToolState.chooseAnchorEnd) {
        leafletMap.value?.dragging.enable();
        // stop rectangle selection if the user just clicked on the map
        // (if both corners of the rectangle are the same)
        if (toolAnchorStart.value && event.latlng.equals(toolAnchorStart.value)) {
          resetTools();
          return;
        }
        toolAnchorEnd.value = event.latlng;
        toolState.value = ToolState.finished;
      }
    }

    function handleMapMouseMove(event: LeafletMouseEvent): void {
      if (toolState.value === ToolState.chooseAnchorEnd) {
        event.originalEvent.stopPropagation();
        toolAnchorEnd.value = event.latlng;
      }
    }

    function resetTools(): void {
      toolAnchorStart.value = null;
      toolAnchorEnd.value = null;
      toolState.value = ToolState.finished;
      mapStore.setMarkedStations([]);
    }

    function zoomOntoStations(): void {
      if (!leafletMap.value) return
      leafletMap.value.fitBounds(
        stationStore.allStations.map((s) => [s.lat, s.lon]),
        zoomOptions
      );
    }

    async function handleStartDateSelected(filter: Date): Promise<void> {
      stationStore.putFilter({ startDate: filter });
      await stationStore.fetchFilteredStations();
    }

    async function handleEndDateSelected(filter: Date): Promise<void> {
      stationStore.putFilter({ endDate: filter });
      await stationStore.fetchFilteredStations();
    }

    return {
      t,
      zoomOptions,
      lMap,
      datasetStore,
      stationStore,
      mapStore,
      Tool: ToolConst,
      ToolState: ToolStateConst,
      currentTool,
      rectangleToolPurpose,
      circleToolPurpose,
      toolState,
      toolAnchorStart,
      toolAnchorEnd,
      rectangleToolItems,
      circleToolItems,
      toolPurpose,
      allStationsWithStatus,
      emitHide,
      setLMap,
      updateTool,
      updateToolState,
      updateToolPurpose,
      handleUnselectStations,
      handleKeyUp,
      handleMouseDown,
      handleMouseUp,
      handleMapMouseMove,
      resetTools,
      zoomOntoStations,
      handleStartDateSelected,
      handleEndDateSelected,
      clusterReady,
      drawingToolCursor,
    };
  },
});
</script>

<style scoped>
.retract-button {
  padding: 0 !important;
  position: absolute;
  z-index: 1000;
  background: white;
  top: calc(50% - 25px);
  height: 50px !important;
  width: 20px !important;
  min-width: 20px !important;
  display: flex;
  border-radius: 0 10px 10px 0;
  border: 1px solid rgb(var(--v-theme-primary));
  border-left: none;
}

.position-relative {
  position: relative;
}

.map-button {
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  padding: 0 !important;
}
</style>
